'use client';

import { useState } from 'react';

import { AxiosError } from 'axios';
import { camelizeKeys } from 'humps';

import {
  WagContentApiEndpoint,
  WAG_CONTENT_API_URL,
} from '../../constants/endpoints';

import { ArticleResponse } from '../../types/api/article';

import { tryCatch } from '../../utils/tryCatch';
import { applyPathParams, httpGet } from '../../wag-react/common/api';

export type DailyBlogArticleCommmentList = ArticleResponse['commentList'];
export const getDailyWagBlogArticleComments = async (
  slug: string,
  page = 1,
): Promise<DailyBlogArticleCommmentList> => {
  const endpoint = applyPathParams(
    WagContentApiEndpoint.DailyWagBlogArticleComments,
  )({
    slug,
  });

  const response = await httpGet<ArticleResponse['commentList']>(endpoint, {
    baseURL: WAG_CONTENT_API_URL,
    params: {
      page,
    },
  });

  const camelizedResponse = camelizeKeys(response.data) as ArticleResponse['commentList'];
  return camelizedResponse;
};

export const useGetDailyWagBlogArticleComments = ({
  initialComments = [],
  initialHasNextPage = false,
  slug = '',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(initialComments);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);
  // Start the page on `2` because we already have the initial comments from the server
  const [page, setPage] = useState(2);

  const execute = async (
    newPage = undefined,
  ): Promise<[DailyBlogArticleCommmentList, AxiosError]> => {
    setIsLoading(true);
    setError(null);

    const [response, responseError] = await tryCatch(() => (
      getDailyWagBlogArticleComments(slug, newPage)
    ));

    if (responseError) {
      setIsLoading(false);
      setError(responseError);
      return [response, responseError as AxiosError];
    }

    setData((prev) => [
      ...prev,
      ...response.items,
    ]);
    setHasNextPage(response.hasNextPage);
    setIsLoading(false);

    return [response, responseError as AxiosError];
  };

  const getNextPage = async () => {
    const [response, responseError] = await execute(page);
    setData((prev) => [
      ...prev,
      ...response.items,
    ]);
    setPage((prev) => prev + 1);
    setHasNextPage(response.hasNextPage);

    return [response, responseError];
  };

  return {
    isLoading,
    error,
    data,
    setData,
    hasNextPage,
    getNextPage,
    execute,
  };
};
